import { cyrb53 } from "./EncryptDecrypt";

/**
 * Privacy-Friendly Daily Fingerprint Generator
 * 
 * Generates a hash that:
 * - Changes every day (no cross-day tracking)
 * - Counts unique visitors within a day
 * - Uses minimal browser data (no canvas/audio)
 * - GDPR compliant without consent
 * 
 * Similar to Plausible Analytics approach
 */

/**
 * Get basic browser data for fingerprinting
 * Uses only standard, non-invasive browser properties
 */
const getBasicBrowserData = () => {
    const components = [];
    
    // Basic browser identification (low entropy)
    components.push(navigator.userAgent || '');
    components.push(navigator.language || '');
    components.push(screen.colorDepth || '');
    components.push(screen.width + 'x' + screen.height);
    components.push(new Date().getTimezoneOffset());
    
    // Don't use:
    // - Canvas fingerprinting (too invasive)
    // - Audio fingerprinting (too invasive)
    // - Hardware-specific identifiers
    
    return components.join('###');
};

/**
 * Get current date string for daily rotation
 * Format: YYYYMMDD
 */
const getDailyDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
};

/**
 * Generate privacy-friendly daily fingerprint
 * 
 * This creates a unique identifier for counting visitors within a day,
 * but the same visitor will have a different identifier tomorrow.
 * 
 * @param {string} dailySalt - Server-provided salt that rotates daily
 * @returns {Promise<string>} Hash that changes every day
 */
export const getDailyFingerprint = async (dailySalt = '') => {
    try {
        // Basic browser data (minimal, non-invasive)
        const browserData = getBasicBrowserData();
        
        // Current date ensures hash changes daily
        const dateString = getDailyDateString();
        
        // Combine with daily salt from server
        // Format: browserData + date + salt
        const dataToHash = `${browserData}${dateString}${dailySalt}`;
        
        // Generate hash
        const hash = cyrb53(dataToHash, 31).toString();
        
        return hash;
        
    } catch (error) {
        console.error('Failed to generate daily fingerprint:', error);
        // Fallback: random value (won't persist, but allows analytics to work)
        return Math.random().toString(36).substring(2);
    }
};

/**
 * Fetch daily salt from server
 * The server generates a new salt every 24 hours
 * 
 * @returns {Promise<string>} Daily salt string
 */
export const fetchDailySalt = async () => {
    try {
        const response = await fetch('/api/v2/statistics/salt');
        const data = await response.json();
        return data.salt || '';
    } catch (error) {
        console.warn('Failed to fetch daily salt, using fallback', error);
        // Fallback: use date only (less unique but still works)
        return '';
    }
};

/**
 * Main function: Get privacy-friendly fingerprint
 * 
 * @returns {Promise<string>} Daily rotating fingerprint hash
 */
export const getPrivacyFriendlyFingerprint = async () => {
    // Fetch today's salt from server
    const dailySalt = await fetchDailySalt();
    
    // Generate daily fingerprint
    const fingerprint = await getDailyFingerprint(dailySalt);
    
    return fingerprint;
};
